/* CS-76: the PDP gallery, from Splide.js to native CSS scroll-snap.
 *
 * The last Splide instance on the default product template. Same trade as CS-71: the phone's own
 * momentum, rubber-band and snap replace a JS reimplementation of all three. The coupled behaviours
 * the Splide product extension carried are re-implemented here, smaller:
 *
 *   - variant change -> scroll to that variant's featured media
 *     (product.js watches `currentMediaId` and dispatches `cascade:product:mediachange`; the
 *      `slideshow_id` in that event comes from `.splide--product`, which stays on the root AS A
 *      SELECTOR HOOK ONLY. Without the bare `.splide` class nothing mounts on it.)
 *   - scroll/swipe -> `cascade:product:slidechange` so product.js updates `currentMediaId`
 *     (product.js's own watcher then fires mediaHidden/mediaVisible, which pauses videos; this file
 *      never needs to touch media wrappers.)
 *   - thumbnail sync: `.is-active` on the current thumb (the bundle CSS border rule keys off
 *     `.splide--thumbnails .is-active a`, so that class also stays as a styling hook)
 *   - arrows page one slide, disabled at the edges (house behaviour per CS-52/CS-71; Splide's
 *     `rewind: true` looping is deliberately NOT kept)
 *   - `data-first-media`: instant scroll when the featured media is not the first slide
 */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function behaviour() {
    return reducedMotion.matches ? 'auto' : 'smooth';
  }

  function wire(root) {
    // Same guard as cs-rail.js: `shopify:section:load` re-runs init over the whole document, and
    // an unguarded second wire() would double every listener on untouched roots.
    if (root.dataset.csGalleryWired) return;
    root.dataset.csGalleryWired = '1';

    const rail = root.querySelector('.cs-gallery__list');
    if (!rail) return;
    const slides = () => Array.from(rail.querySelectorAll('.cs-gallery__slide'));

    const thumbsRoot = root.dataset.thumbnails
      ? document.getElementById(root.dataset.thumbnails)
      : null;

    function slideForMedia(mediaId) {
      return slides().find(s => s.dataset.mediaId === String(mediaId)) || null;
    }

    function scrollToSlide(slide, instant) {
      if (!slide) return;
      rail.scrollTo({ left: slide.offsetLeft - rail.offsetLeft, behavior: instant ? 'auto' : behaviour() });
    }

    // ---- current-slide tracking -------------------------------------------------------------
    let currentIndex = 0;

    const prev = root.querySelector('[data-cs-gallery-arrows] .splide__arrow--prev');
    const next = root.querySelector('[data-cs-gallery-arrows] .splide__arrow--next');

    function syncArrows() {
      if (prev) prev.disabled = currentIndex <= 0;
      if (next) next.disabled = currentIndex >= slides().length - 1;
    }

    function syncThumbs(mediaId) {
      if (!thumbsRoot) return;
      thumbsRoot.querySelectorAll('li').forEach(li => {
        const active = li.dataset.mediaId === String(mediaId);
        li.classList.toggle('is-active', active);
        const link = li.querySelector('a');
        if (link) {
          if (active) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        }
      });
    }

    const io = new IntersectionObserver(
      entries => {
        // The most-visible slide is the current one. 0.6 keeps the 75%-width mobile slides
        // unambiguous: only the snapped slide can cross it.
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const slide = entry.target;
          const index = slides().indexOf(slide);
          if (index === -1 || index === currentIndex) return;
          currentIndex = index;
          syncArrows();
          syncThumbs(slide.dataset.mediaId);
          const section = root.closest('[data-product-section]');
          if (section) {
            section.dispatchEvent(
              new CustomEvent('cascade:product:slidechange', {
                bubbles: true,
                detail: { currentMediaId: Number(slide.dataset.mediaId) },
              })
            );
          }
        });
      },
      { root: rail, threshold: 0.6 }
    );
    slides().forEach(s => io.observe(s));

    // ---- arrows -----------------------------------------------------------------------------
    function page(delta) {
      const all = slides();
      const target = all[Math.min(all.length - 1, Math.max(0, currentIndex + delta))];
      scrollToSlide(target, false);
    }
    if (prev) prev.addEventListener('click', () => page(-1));
    if (next) next.addEventListener('click', () => page(1));

    // ---- variant -> media -------------------------------------------------------------------
    document.addEventListener('cascade:product:mediachange', e => {
      const targetRoot = e.detail && e.detail.slideshow_id
        ? document.getElementById(e.detail.slideshow_id)
        : null;
      if (targetRoot !== root) return;
      const slide = slideForMedia(e.detail.media_id);
      // W3 (founder 18 Aug): product.js echoes our own slidechange straight back as a
      // mediachange. Mid-swipe that echo said "smooth-scroll to the slide the finger is already
      // on", fighting the drag and reading as jerk. The slide the IO just made current needs no
      // scroll at all; a genuine variant switch to a DIFFERENT slide still scrolls.
      if (!slide || slides().indexOf(slide) === currentIndex) return;
      scrollToSlide(slide, false);
    });

    // ---- initial position -------------------------------------------------------------------
    const first = root.dataset.firstMedia && slideForMedia(root.dataset.firstMedia);
    if (first && slides().indexOf(first) > 0) {
      scrollToSlide(first, true);
      currentIndex = slides().indexOf(first);
    }
    syncArrows();
    if (first) syncThumbs(first.dataset.mediaId);
    else if (slides()[0]) syncThumbs(slides()[0].dataset.mediaId);
  }

  function init() {
    document.querySelectorAll('[data-cs-gallery]').forEach(wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
